import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';

import { cx } from './cx';
import { errorIdOf, useFieldError } from './fieldProblem';
import { Icon } from './Icon';

/**
 * A label above a control, and the sentence under it that stops the control being
 * mysterious.
 *
 * The control is cloned with an id rather than every caller writing the same id twice,
 * because the half of that pair that gets forgotten is exactly the half a screen reader
 * needs. The id is generated unless the caller gives one — and a caller gives one for the
 * reason `fieldProblem.ts` explains: a refusal from the server names a field, and a control
 * whose id is that name turns red with the reason written under it. A dialog's fields are
 * all inside `Field`, so without this a refused save in any dialog could only be a banner.
 */
export function Field({
  id: given,
  also = [],
  label,
  help,
  className,
  children,
}: {
  /** The API's name for the field, when a refusal about it should land here. */
  id?: string;
  /**
   * Other names a refusal might use for what this field holds — a pair of time boxes in one
   * field, say — so the sentence appears under it for those too. The controls carry those
   * ids themselves.
   */
  also?: readonly string[];
  label: ReactNode;
  help?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const generated = useId();
  const id = given ?? generated;
  const problem = useFieldError([id, ...also]);

  return (
    <div className={cx('flex flex-col', className)}>
      <label htmlFor={id} className="text-secondary font-medium text-ink-2 mb-1">
        {label}
      </label>
      {isValidElement(children)
        ? cloneElement(children as ReactElement<{ id?: string }>, { id })
        : children}
      {help ? <span className="text-secondary text-ink-3 mt-1">{help}</span> : null}
      {problem ? (
        <span
          id={errorIdOf(problem.id)}
          className="flex items-center gap-1.5 text-secondary font-medium text-danger mt-1"
        >
          <Icon name="alert" small />
          <span className="min-w-0">{problem.message}</span>
        </span>
      ) : null}
    </div>
  );
}

/**
 * The same mark, for something that is not a control: the dispatch ladder, the map a home
 * view is chosen on, the boundary drawn on a map. The server refuses those by name like any
 * field, and this gives the name something to land on — the block tints, the sentence
 * appears under it, and `FieldErrors` can scroll to it because the element carries the id.
 */
export function Flagged({
  id,
  also = [],
  label,
  className,
  children,
}: {
  id: string;
  also?: readonly string[];
  /** What the block is called, for the banner that names a refused field and for a screen reader. */
  label?: string;
  className?: string;
  children: ReactNode;
}) {
  const problem = useFieldError([id, ...also]);

  return (
    <div
      id={id}
      role={label === undefined ? undefined : 'group'}
      aria-label={label}
      className={cx(problem ? 'rounded-card bg-danger-soft' : '', className)}
    >
      {children}
      {problem ? (
        <div
          id={errorIdOf(problem.id)}
          className="flex items-center gap-1.5 text-secondary font-medium text-danger mt-1.5 px-1"
        >
          <Icon name="alert" small />
          <span className="min-w-0">{problem.message}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Only the sentence, for a control that sits in a row of its own rather than inside a
 * `Field` — one number among a list of lines, say. The control carries the id; this goes
 * under it, and says nothing until a refusal names that id.
 */
export function FieldMessage({ id }: { id: string }) {
  const problem = useFieldError([id]);

  // The icon on the first line: a row's box is narrow, and its reason often wraps.
  return problem ? (
    <span
      id={errorIdOf(problem.id)}
      className="flex items-start gap-1.5 text-secondary font-medium text-danger mt-1"
    >
      <span className="mt-[3px] shrink-0">
        <Icon name="alert" small />
      </span>
      <span className="min-w-0">{problem.message}</span>
    </span>
  ) : null;
}
